import React, { useState, useEffect } from 'react';
import { Play, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Quotes from '../components/Quotes';

/* ─────────────────────────────────────────────
   Marketplace icon dots (uses real /icons/*.png)
───────────────────────────────────────────── */
const MarketplaceIcons = ({ platforms }) => {
  if (!platforms || platforms.length === 0) return null;
  return (
    <span className="inline-flex items-center gap-1 ml-2">
      {platforms.map((p, i) => (
        <img
          key={i}
          src={`/icons/${p}.png`}
          alt={p}
          className="h-5 w-5 object-contain rounded"
          title={p.charAt(0).toUpperCase() + p.slice(1)}
        />
      ))}
    </span>
  );
};

/* ─────────────────────────────────────────────
   Reusable checkmark icon
───────────────────────────────────────────── */
const Check = ({ color }) => (
  <svg
    className="w-5 h-5 flex-shrink-0 mt-0.5"
    fill={color}
    viewBox="0 0 20 20"
  >
    <path
      fillRule="evenodd"
      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
      clipRule="evenodd"
    />
  </svg>
);

/* ─────────────────────────────────────────────
   Theme tokens per product
───────────────────────────────────────────── */
const THEMES = {
  msp: {
    gradient: 'linear-gradient(90deg, rgb(20,184,166) 0%, rgb(37,99,235) 100%)',
    gradientHover: 'linear-gradient(90deg, rgb(15,161,148) 0%, rgb(29,78,216) 100%)',
    featuredBorder: 'border-teal-400',
    checkColor: 'rgba(50,121,214,1)',
    priceClass: 'from-teal-500 to-blue-600',
    badgeBg: 'bg-teal-500',
    ctaOutline: 'border-gray-900 text-gray-900 hover:bg-gray-50',
    ctaFill: null, // uses gradient inline
    ctaFillStyle: { background: 'linear-gradient(90deg, rgb(20,184,166) 0%, rgb(37,99,235) 100%)' },
    unlimitedBadge: 'bg-gradient-to-r from-teal-500 to-blue-600',
    demoBtn: 'from-red-600 to-pink-600',
    comingSoonAccent: 'text-blue-600',
    label: 'MultiSellerPro',
    icon: '🛒',
  },
  mip: {
    gradient: 'linear-gradient(135deg, #2563eb, #7c3aed)',
    gradientHover: 'linear-gradient(135deg, #1d4ed8, #6d28d9)',
    featuredBorder: 'border-blue-500',
    checkColor: '#4f46e5',
    priceClass: 'from-blue-600 to-purple-600',
    badgeBg: 'bg-blue-600',
    ctaOutline: 'border-blue-700 text-blue-700 hover:bg-blue-50',
    ctaFill: null,
    ctaFillStyle: { background: 'linear-gradient(135deg, #2563eb, #7c3aed)' },
    unlimitedBadge: 'bg-gradient-to-r from-blue-600 to-purple-600',
    demoBtn: 'from-blue-700 to-purple-700',
    comingSoonAccent: 'text-indigo-600',
    label: 'MultiInfluencerPro',
    icon: '🎬',
  },
  bundle: {
    gradient: 'linear-gradient(90deg, #b45309, #c2410c)',
    featuredBorder: 'border-amber-400',
    checkColor: '#b45309',
    priceClass: 'from-amber-600 to-orange-600',
    badgeBg: 'bg-amber-500',
    ctaFill: null,
    ctaFillStyle: { background: 'linear-gradient(90deg, #b45309, #c2410c)' },
    demoBtn: 'from-amber-700 to-orange-700',
    comingSoonAccent: 'text-amber-700',
    label: 'Bundle',
    icon: '🔥',
  },
};

/* ─────────────────────────────────────────────
   Feature lists
───────────────────────────────────────────── */
const MSP_BEGINNER_FEATURES = [
  { text: '2 Chrome Extension Installs', platforms: ['amazon', 'walmart', 'ebay', 'aip'] },
  { text: '2 Mobile App Installs', platforms: ['amazon', 'walmart', 'ebay', 'aip'] },
  { text: 'MSP Ungate Machine', platforms: ['amazon'] },
  { text: 'MSP Local Scan', platforms: [], watchUrl: 'https://www.youtube.com/shorts/RwE7Kpis9pU' },
  { text: 'eBay Lister (Single & Bulk)', platforms: ['ebay'] },
  { text: 'Custom Additional Tools (ITS/Labels/etc.)', platforms: ['amazon', 'walmart'] },
];
const MSP_PRO_FEATURES = [
  { text: 'My Amazon Ungated Brands', platforms: ['amazon'] },
  { text: 'Bulk Source Ungated Brands', platforms: ['amazon'] },
  { text: 'MSP Ungate Crawler!!!', platforms: ['amazon'] },
  { text: 'Universal Live Scan (Scan Any Website)', platforms: ['amazon', 'walmart', 'ebay'] },
  { text: 'Universal Reverse Scan (Scan Any Website)', platforms: ['amazon', 'walmart', 'ebay'] },
  { text: 'Auto Reverse Scan (10 Suppliers)', platforms: ['amazon', 'walmart', 'ebay'] },
  { text: 'Storefront Scan (25 Stores)', platforms: ['amazon', 'walmart', 'ebay'] },
  { text: 'Wholesale Scan (50K Items)', platforms: ['amazon', 'walmart', 'ebay'] },
  { text: 'Amazon to Amazon Flips + A2W', platforms: ['amazon', 'walmart'] },
  { text: 'Keepa On-page Gated Status Data!', platforms: ['amazon'] },
];
const MSP_UNLIMITED_FEATURES = [
  { text: 'MSP Inventory!!!', platforms: ['amazon', 'walmart', 'ebay'] },
  { text: 'Live Scan (Background Scan)', platforms: ['amazon', 'walmart', 'ebay'] },
  { text: 'Auto Reverse Scan (Unlimited)', platforms: ['amazon', 'walmart', 'ebay'] },
  { text: 'Storefront Scan (50+ Stores)', platforms: ['amazon', 'walmart', 'ebay'] },
  { text: 'Wholesale Scan (150K+ Items)', platforms: ['amazon', 'walmart', 'ebay'] },
  { text: 'Alert System!!! (Stock/Seller/Price)', platforms: ['amazon', 'walmart'] },
  { text: 'Reconciling, Labels, Reports, etc.', platforms: ['amazon', 'walmart'] },
  { text: "Request any tool - we'll work on it!", platforms: [] },
];

const MIP_BEGINNER_FEATURES = [
  { text: '2 AIP Chrome Extension (Many Features)', platforms: ['aip'] },
  { text: '2 AIP Mobile App', platforms: ['aip'] },
  { text: 'AIP Video Studio', platforms: ['aip'] },
  { text: 'Auto Post (ASINs tag + Title etc.)', platforms: ['aip'] },
  { text: 'Bulk Video Recording', platforms: ['aip'] },
  { text: 'Auto Post Affiliated YouTube Video', platforms: ['aip'] },
  { text: 'AIP Product eBay Listing Creation', platforms: ['aip'] },
];
const MIP_PRO_FEATURES = [
  { text: 'Generate Thumbnails (100)', platforms: ['aip'] },
  { text: 'AIP Storefront Scans (25 Stores)', platforms: ['aip'] },
  { text: 'All Creator Connections Features!', platforms: ['aip'] },
  { text: 'CC Power Search + Bulk Messaging', platforms: ['aip'] },
  { text: 'Find Campaigns for Your Videos + Auto-Accept, Link & Follow Up', platforms: ['aip'] },
  { text: 'All Amazon.com On-Page Data & Scans', platforms: ['aip'] },
  { text: 'Our Amazing Messaging System!', platforms: ['aip'] },
  { text: 'Basically everything you need for AIP! :)', platforms: ['aip'] },
];
const MIP_UNLIMITED_FEATURES = [
  { text: 'Generate Thumbnails (250+)', platforms: ['aip'] },
  { text: 'AIP Storefront Scans (50+ Stores)', platforms: ['aip'] },
  { text: 'Bulk Search Page Data', platforms: ['aip'] },
  { text: 'Order History Page Data', platforms: ['aip'] },
  { text: 'Bulk Video Upload', platforms: ['aip'] },
  { text: 'CSV Scan', platforms: ['aip'] },
  { text: 'Media Kit & More!', platforms: ['aip'] },
  { text: "Request any tool - we'll work on it!", platforms: [] },
];

/* ─────────────────────────────────────────────
   Tutorial videos per section
───────────────────────────────────────────── */
const MSP_TUTORIALS = [
  { title: 'MSP Chrome Extension', url: 'https://www.youtube.com/embed/RkcCIAh6-PA', platforms: ['amazon', 'walmart', 'ebay', 'aip'], plan: 'Beginner' },
  { title: 'MSP Mobile App', url: 'https://www.youtube.com/embed/RkcCIAh6-PA', platforms: ['amazon', 'walmart', 'ebay', 'aip'], plan: 'Beginner' },
  { title: 'MSP Ungate Machine', url: 'https://www.youtube.com/embed/BFABuuhvw9E', platforms: ['amazon'], plan: 'Beginner' },
  { title: 'MSP Local Scan', url: 'https://www.youtube.com/embed/k5Kn9Ko7o-k', platforms: [], plan: 'Beginner' },
  { title: 'Live Scan', url: 'https://www.youtube.com/embed/rtlDOpvow3s', platforms: ['amazon', 'walmart', 'ebay'], plan: 'Pro' },
  { title: 'Reverse Scan', url: 'https://www.youtube.com/embed/taKWlLvPBUI', platforms: ['amazon', 'walmart', 'ebay'], plan: 'Pro' },
  { title: 'Storefront Scan', url: 'https://www.youtube.com/embed/at8bxgKZdYE', platforms: ['amazon', 'walmart', 'ebay'], plan: 'Pro' },
  { title: 'Wholesale Scan', url: 'https://www.youtube.com/embed/-bp1tvKplD0', platforms: ['amazon', 'walmart', 'ebay'], plan: 'Pro' },
  { title: 'Amazon to Amazon (A2A)', url: 'https://www.youtube.com/embed/JWLRxRK_Acw', platforms: ['amazon'], plan: 'Pro' },
];

const MIP_TUTORIALS = [
  { title: 'AIP Chrome Extension', url: 'https://www.youtube.com/embed/fCeFGZrjX0U', platforms: ['aip'], plan: 'Beginner' },
  { title: 'AIP Mobile App', url: 'https://www.youtube.com/embed/fCeFGZrjX0U', platforms: ['aip'], plan: 'Beginner' },
];

/* ─────────────────────────────────────────────
   Product ID maps
───────────────────────────────────────────── */
const MSP_PRODUCT_IDS = {
  Beginner: { monthly: { withTrial: '131671', noTrial: '131685' }, annual: { withTrial: '131679', noTrial: '131691' } },
  Pro: { monthly: { withTrial: '131675', noTrial: '131687' }, annual: { withTrial: '131681', noTrial: '131693' } },
  Unlimited: { monthly: { withTrial: '131677', noTrial: '131689' }, annual: { withTrial: '131683', noTrial: '131695' } },
};
const MIP_PRODUCT_IDS = {
  Beginner: { monthly: { withTrial: '131674', noTrial: '131686' }, annual: { withTrial: '131680', noTrial: '131692' } },
  Pro: { monthly: { withTrial: '131676', noTrial: '131688' }, annual: { withTrial: '131682', noTrial: '131694' } },
  Unlimited: { monthly: { withTrial: '131678', noTrial: '131690' }, annual: { withTrial: '131684', noTrial: '131696' } },
};
const BUNDLE_PRODUCT_IDS = {
  Beginner: { monthly: { withTrial: '126335', noTrial: '126388' }, annual: { withTrial: '126338', noTrial: '126391' } },
  Pro: { monthly: { withTrial: '126336', noTrial: '126389' }, annual: { withTrial: '126339', noTrial: '126392' } },
  Unlimited: { monthly: { withTrial: '126337', noTrial: '126390' }, annual: { withTrial: '126340', noTrial: '126393' } },
};

/* ─────────────────────────────────────────────
   Helper: build checkout URL
───────────────────────────────────────────── */
function buildCheckoutUrl(productIdMap, tier, billingPeriod, hasHadTrial, email) {
  const periodKey = billingPeriod === 'annual' ? 'annual' : 'monthly';
  const trialKey = hasHadTrial ? 'noTrial' : 'withTrial';
  const productId = productIdMap[tier][periodKey][trialKey];
  const encoded = encodeURIComponent(email);
  return `https://store.payproglobal.com/checkout?products[1][id]=${productId}&page-template=21298&language=en&currency=USD&billing-email=${encoded}`;
}

/* ─────────────────────────────────────────────
   Price display helpers
───────────────────────────────────────────── */
const BASE_PRICES = {
  msp: { Beginner: 24, Pro: 54, Unlimited: 74 },
  mip: { Beginner: 19, Pro: 39, Unlimited: 54 },
};

const WAS_PRICES_ORIGINAL = {
  msp: {
    monthly: { Beginner: 39, Pro: 69, Unlimited: 99 },
    annual: { Beginner: 390, Pro: 690, Unlimited: 990 },
  },
  mip: {
    monthly: { Beginner: 29, Pro: 49, Unlimited: 79 },
    annual: { Beginner: 290, Pro: 490, Unlimited: 790 },
  },
};

function nowPrice(tier, billingPeriod, product = 'msp') {
  return billingPeriod === 'annual'
    ? BASE_PRICES[product][tier] * 10
    : BASE_PRICES[product][tier];
}

function wasPrice(tier, billingPeriod, product = 'msp') {
  return WAS_PRICES_ORIGINAL[product][billingPeriod][tier];
}
function bundleNow(tier, billingPeriod) {
  const monthly = tier === 'Unlimited' ? 98 : Math.round((nowPrice(tier, 'monthly') + nowPrice(tier, 'monthly', 'mip')) * 0.8);
  return billingPeriod === 'annual' ? monthly * 10 : monthly;
}
function bundleWas(tier, billingPeriod) {
  return wasPrice(tier, billingPeriod) + wasPrice(tier, billingPeriod, 'mip');
}

/* ─────────────────────────────────────────────
   FeatureList
───────────────────────────────────────────── */
const FeatureList = ({ features, checkColor }) => (
  <ul className="space-y-3">
    {features.map((f, i) => (
      <li key={i} className="flex items-start gap-3">
        <Check color={checkColor} />
        <span className="text-gray-700 text-sm flex items-center flex-wrap gap-1">
          {f.text}
          {f.watchUrl && (
            <a
              href={f.watchUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-red-500 hover:text-red-600 font-semibold text-xs border border-red-300 hover:border-red-500 rounded-full px-2 py-0.5 transition ml-1"
            >
              <Play size={10} fill="currentColor" />
              WATCH
            </a>
          )}
          {f.platforms && <MarketplaceIcons platforms={f.platforms} />}
        </span>
      </li>
    ))}
  </ul>
);

/* ─────────────────────────────────────────────
   PriceBlock
───────────────────────────────────────────── */
function formatPrice(n) {
  const rounded = Math.round(n * 10) / 10;
  return Number.isInteger(rounded) ? rounded.toString() : rounded.toFixed(1);
}

const PriceBlock = ({ tier, billingPeriod, priceClass, isBundle = false, product = 'msp' }) => {
  const now = isBundle ? bundleNow(tier, billingPeriod) : nowPrice(tier, billingPeriod, product);
  const was = isBundle ? bundleWas(tier, billingPeriod) : wasPrice(tier, billingPeriod, product);
  const isAnnual = billingPeriod === 'annual';
  const displayNow = isAnnual ? formatPrice(now / 12) : now;
  const displayWas = isAnnual ? formatPrice(was / 12) : was;

  return (
    <div className="mb-8">
      <div className="flex flex-wrap items-baseline gap-2 mb-1">
        <span className="text-red-500 line-through text-xl font-semibold">${displayWas}</span>
        <span className={`text-5xl font-bold bg-gradient-to-r ${priceClass} bg-clip-text text-transparent`}>
          ${displayNow}
        </span>
        <span className="text-gray-500 text-lg">/mo</span>
        {billingPeriod === 'monthly' && (
          <span className="text-red-500 text-lg w-full sm:w-auto">for a limited time!</span>
        )}
      </div>
      {isAnnual && (
        <>
          <p className="text-gray-600 text-sm">*Billed annually</p>
          <p className="text-green-600 font-semibold text-sm">2 months free with annual billing!</p>
        </>
      )}
    </div>
  );
};

/* ─────────────────────────────────────────────
   DemoButton
───────────────────────────────────────────── */
const DemoButton = ({ gradient, onClick }) => (
  <button
    onClick={onClick}
    className={`mt-6 bg-gradient-to-r ${gradient} text-white px-6 py-1 rounded-lg font-semibold hover:opacity-90 transition shadow-lg flex items-center justify-center gap-2 text-sm`}
  >
    <Play size={18} fill="white" />
    Watch Feature Demos
  </button>
);

/* ─────────────────────────────────────────────
   VideoModal - shared for MSP and MIP demos
───────────────────────────────────────────── */
const VideoModal = ({ show, onClose, tutorials, plan, title, gradient }) => {
  if (!show) return null;
  const filtered = tutorials.filter(v => v.plan === plan);
  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-70 z-50" onClick={onClose} />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden" style={{ animation: 'fadeIn 0.2s ease-out' }}>
          <div className={`bg-gradient-to-r ${gradient} p-4 flex items-center justify-between`}>
            <h2 className="text-2xl font-bold text-white flex items-center gap-3">
              <Play size={24} fill="white" />
              {title} - Feature Demos
            </h2>
            <button
              onClick={onClose}
              className="w-10 h-10 bg-white/20 hover:bg-white/30 text-white rounded-full flex items-center justify-center transition"
            >
              <X size={24} />
            </button>
          </div>
          <div className="p-6 overflow-y-auto max-h-[calc(90vh-100px)] bg-gray-50">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map((video, idx) => (
                <div key={idx} className="bg-white rounded-xl overflow-hidden shadow-md hover:shadow-xl transition">
                  <div className="relative bg-gray-900">
                    <iframe
                      width="100%" height="200"
                      src={video.url}
                      title={video.title}
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      className="w-full"
                    />
                  </div>
                  <div className="flex p-4 bg-white items-center">
                    <p className="font-bold text-gray-900 text-sm">{video.title}</p>
                    {video.platforms.length > 0 && (
                      <div className="ml-auto">
                        <MarketplaceIcons platforms={video.platforms} />
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

/* ─────────────────────────────────────────────
   Non-US Marketplace Card
───────────────────────────────────────────── */
const MARKETPLACE_LABELS = {
  UK: { code: 'UK', flag: '🇬🇧', name: 'United Kingdom', domain: '.co.uk' },
  CA: { code: 'CA', flag: '🇨🇦', name: 'Canada', domain: '.ca' },
  MX: { code: 'MX', flag: '🇲🇽', name: 'Mexico', domain: '.com.mx' },
};

const NON_US_PLANS = [
  {
    tier: 'Beginner',
    sections: [
      { title: 'Ungating Suite', features: ['Ungate Crawler', 'My Ungated Brands', 'Keepa Gated Status Extension', 'Ungate Machine'] },
      { title: 'Apps / Extension', features: ['Chrome Extension', 'Mobile App'] },
    ],
    soon: ['Universal Live Scan', 'Universal Reverse Scan', 'Amazon to Amazon Flips (A2A)'],
  },
  {
    tier: 'Pro',
    sections: [
      { title: 'Ungating Suite', features: ['Ungate Crawler', 'My Ungated Brands', 'Keepa Gated Status Extension', 'Ungate Machine'] },
      { title: 'Apps / Extension', features: ['Chrome Extension', 'Mobile App'] },
      { title: 'MSP Scans', features: ['Wholesale Scan', 'Storefront Scan'] },
    ],
    soon: ['Universal Live Scan', 'Universal Reverse Scan', 'Amazon to Amazon Flips (A2A)'],
  },
];

const NonUSCard = ({ billingPeriod, user, onUpgrade, marketplaceCode }) => {
  const t = THEMES.msp;
  const mp = MARKETPLACE_LABELS[marketplaceCode];

  return (
    <div className="md:col-span-3 max-w-2xl mx-auto w-full">
      <div className={`bg-white rounded-3xl shadow-xl p-8 border-2 ${t.featuredBorder} relative`}>

        {/* Header */}
        <div className="flex items-center gap-3 mb-1">
          <span className="text-4xl">{mp.flag}</span>
          <div>
            <h2 className="text-2xl font-bold text-gray-900">{mp.code} Plan</h2>
            <p className="text-gray-400 text-sm">amazon{mp.domain}</p>
          </div>
        </div>

      </div>

      {/* Two cards side by side */}
      <div className="grid md:grid-cols-2 gap-6 mt-6">
        {NON_US_PLANS.map(p => {
          const isCurrent = user?.subscription_plan === `MSP_${p.tier}` && user?.subscription_active;
          return (
            <div key={p.tier} className={`bg-white rounded-2xl p-6 border-2 ${p.tier === 'Pro' ? t.featuredBorder + ' shadow-xl' : 'border-gray-200 shadow-lg'}`}>
              <h3 className="text-xl font-bold text-gray-900 mb-1">MSP {p.tier}</h3>
              <PriceBlock tier={p.tier} billingPeriod={billingPeriod} priceClass={t.priceClass} />
              <button
                onClick={() => onUpgrade(p.tier, 'msp')}
                className={`w-full py-3 rounded-xl text-base font-semibold transition shadow mb-6 text-white ${isCurrent ? '!bg-gray-300 !text-gray-600' : 'hover:opacity-90'}`}
                style={!isCurrent ? t.ctaFillStyle : {}}
              >
                {isCurrent ? '✓ Current Plan' : 'Subscribe Now'}
              </button>
              <div className="space-y-5">
                {p.sections.map((section, si) => (
                  <div key={si}>
                    <h4 className="text-xs font-semibold text-gray-500 mb-2 flex items-center gap-2">
                      {section.title}
                      <img src="/icons/amazon.png" alt="amazon" className="h-4 w-4 object-contain" />
                    </h4>
                    <ul className="space-y-2">
                      {section.features.map((f, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <Check color={t.checkColor} />
                          <span className="text-gray-700 text-sm">{f}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
                {p.tier == "Pro" && <div>
                  <h4 className="text-xs font-semibold text-gray-400 mb-2">Coming Soon</h4>
                  <ul className="space-y-2">
                    {p.soon.map((f, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <Check color="#9ca3af" />
                        <span className="text-gray-400 text-sm flex items-center gap-2">
                          {f}
                          <img src="/icons/amazon.png" alt="amazon" className="h-4 w-4 object-contain opacity-50" />
                          <span className="text-xs bg-gray-100 text-gray-400 px-2 py-0.5 rounded-full">Soon</span>
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────
   MSP Cards
───────────────────────────────────────────── */
const MSPCards = ({ billingPeriod, freeTrialDays, user, onUpgrade, onDemo }) => {
  const t = THEMES.msp;
  const isCurrentPlan = (tier) => user?.subscription_plan === `MSP_${tier}` && user?.subscription_active;

  const ctaLabel = (tier) => {
    if (isCurrentPlan(tier)) return '✓ Current Plan';
    return freeTrialDays > 0 ? `Start ${freeTrialDays}-Day Free Trial` : 'Subscribe Now';
  };

  return (
    <>
      {/* Beginner */}
      <div className="bg-white rounded-3xl shadow-lg p-8 border border-gray-200 hover:shadow-xl transition-shadow relative">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">MSP Beginner</h2>
        <PriceBlock tier="Beginner" billingPeriod={billingPeriod} priceClass={t.priceClass} />
        <button
          onClick={() => onUpgrade('Beginner', 'msp')}
          className={`w-full py-4 rounded-xl text-lg font-semibold transition mb-8 ${isCurrentPlan('Beginner') ? 'bg-gray-300 text-gray-600' : `bg-white border-2 ${t.ctaOutline}`}`}
        >
          {ctaLabel('Beginner')}
        </button>
        <div className="border-t border-gray-200 pt-6">
          <h3 className="text-sm font-semibold text-gray-500 mb-4">Features Included:</h3>
          <FeatureList features={MSP_BEGINNER_FEATURES} checkColor={t.checkColor} />
          <DemoButton gradient={t.demoBtn} onClick={() => onDemo('Beginner', 'msp')} />
        </div>
      </div>

      {/* Pro */}
      <div className={`bg-white rounded-3xl shadow-xl p-8 border-2 ${t.featuredBorder} relative hover:shadow-2xl transition-shadow`}>
        <h2 className="text-2xl font-bold text-gray-900 mb-6">MSP Pro</h2>
        <PriceBlock tier="Pro" billingPeriod={billingPeriod} priceClass={t.priceClass} />
        <button
          onClick={() => onUpgrade('Pro', 'msp')}
          className={`w-full py-4 rounded-xl text-lg font-semibold transition shadow-lg mb-8 text-white ${isCurrentPlan('Pro') ? '!bg-gray-300 !text-gray-600' : 'hover:opacity-90'}`}
          style={!isCurrentPlan('Pro') ? t.ctaFillStyle : {}}
        >
          {ctaLabel('Pro')}
        </button>
        <div className="border-t border-gray-200 pt-6">
          <h3 className="text-sm font-semibold text-gray-500 mb-4">Everything in Beginner, plus:</h3>
          <FeatureList features={MSP_PRO_FEATURES} checkColor={t.checkColor} />
          <DemoButton gradient={t.demoBtn} onClick={() => onDemo('Pro', 'msp')} />
        </div>
      </div>

      {/* Unlimited */}
      <div className="bg-white rounded-3xl shadow-2xl p-8 border-2 border-teal-400 relative hover:shadow-3xl transition-shadow">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">MSP Unlimited</h2>
        <PriceBlock tier="Unlimited" billingPeriod={billingPeriod} priceClass={t.priceClass} />
        <button
          onClick={() => onUpgrade('Unlimited', 'msp')}
          className={`w-full py-4 rounded-xl text-lg font-semibold transition shadow-lg mb-8 text-white ${isCurrentPlan('Unlimited') ? '!bg-gray-300 !text-gray-600' : 'hover:opacity-90'}`}
          style={!isCurrentPlan('Unlimited') ? { background: 'linear-gradient(90deg, rgb(168,85,247) 0%, rgb(236,72,153) 100%)' } : {}}
        >
          {ctaLabel('Unlimited')}
        </button>
        <div className="border-t border-gray-200 pt-6">
          <h3 className="text-sm font-semibold text-gray-500 mb-4">Everything in Pro, plus:</h3>
          <FeatureList features={MSP_UNLIMITED_FEATURES} checkColor={t.checkColor} />
        </div>
      </div>
    </>
  );
};

/* ─────────────────────────────────────────────
   MIP Cards
───────────────────────────────────────────── */
const MIPCards = ({ billingPeriod, freeTrialDays, user, onUpgrade, onDemo }) => {
  const t = THEMES.mip;
  const isCurrentPlan = (tier) => user?.subscription_plan === `MIP_${tier}` && user?.subscription_active;

  const ctaLabel = (tier) => {
    if (isCurrentPlan(tier)) return '✓ Current Plan';
    return freeTrialDays > 0 ? `Start ${freeTrialDays}-Day Free Trial` : 'Subscribe Now';
  };

  return (
    <>
      {/* Beginner */}
      <div className="bg-white rounded-3xl shadow-lg p-8 border border-gray-200 hover:shadow-xl transition-shadow relative">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">MIP Beginner</h2>
        <PriceBlock tier="Beginner" billingPeriod={billingPeriod} priceClass={t.priceClass} product="mip" />
        <button
          onClick={() => onUpgrade('Beginner', 'mip')}
          className={`w-full py-4 rounded-xl text-lg font-semibold transition mb-8 ${isCurrentPlan('Beginner') ? 'bg-gray-300 text-gray-600' : `bg-white border-2 ${t.ctaOutline}`}`}
        >
          {ctaLabel('Beginner')}
        </button>
        <div className="border-t border-gray-200 pt-6">
          <h3 className="text-sm font-semibold text-gray-500 mb-4">Features Included:</h3>
          <FeatureList features={MIP_BEGINNER_FEATURES} checkColor={t.checkColor} />
        </div>
      </div>

      {/* Pro */}
      <div className={`bg-white rounded-3xl shadow-xl p-8 border-2 ${t.featuredBorder} relative hover:shadow-2xl transition-shadow`}>
        <h2 className="text-2xl font-bold text-gray-900 mb-6">MIP Pro</h2>
        <PriceBlock tier="Pro" billingPeriod={billingPeriod} priceClass={t.priceClass} product="mip" />
        <button
          onClick={() => onUpgrade('Pro', 'mip')}
          className={`w-full py-4 rounded-xl text-lg font-semibold transition shadow-lg mb-8 text-white ${isCurrentPlan('Pro') ? '!bg-gray-300 !text-gray-600' : 'hover:opacity-90'}`}
          style={!isCurrentPlan('Pro') ? t.ctaFillStyle : {}}
        >
          {ctaLabel('Pro')}
        </button>
        <div className="border-t border-gray-200 pt-6">
          <h3 className="text-sm font-semibold text-gray-500 mb-4">Everything in Beginner, plus:</h3>
          <FeatureList features={MIP_PRO_FEATURES} checkColor={t.checkColor} />
        </div>
      </div>

      {/* Unlimited */}
      <div className="bg-white rounded-3xl shadow-2xl p-8 border-2 border-blue-400 relative hover:shadow-3xl transition-shadow">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">MIP Unlimited</h2>
        <PriceBlock tier="Unlimited" billingPeriod={billingPeriod} priceClass={t.priceClass} product="mip" />
        <button
          onClick={() => onUpgrade('Unlimited', 'mip')}
          className={`w-full py-4 rounded-xl text-lg font-semibold transition shadow-lg mb-8 text-white ${isCurrentPlan('Unlimited') ? '!bg-gray-300 !text-gray-600' : 'hover:opacity-90'}`}
          style={!isCurrentPlan('Unlimited') ? { background: 'linear-gradient(135deg, #1e3a8a, #7c3aed)' } : {}}
        >
          {ctaLabel('Unlimited')}
        </button>
        <div className="border-t border-gray-200 pt-6">
          <h3 className="text-sm font-semibold text-gray-500 mb-4">Everything in Pro, plus:</h3>
          <FeatureList features={MIP_UNLIMITED_FEATURES} checkColor={t.checkColor} />
        </div>
      </div>
    </>
  );
};

/* ─────────────────────────────────────────────
   Bundle Cards
───────────────────────────────────────────── */
const BundleCards = ({ billingPeriod, freeTrialDays, user, onUpgrade }) => {
  const t = THEMES.bundle;

  const isCurrentPlan = (tier) => user?.subscription_plan === tier && user?.subscription_active;

  const ctaLabel = (tier) => {
    if (isCurrentPlan(tier)) return '✓ Current Plan';
    return freeTrialDays > 0 ? `Start ${freeTrialDays}-Day Free Trial` : 'Subscribe Now';
  };

  const ProductChips = () => (
    <div className="flex gap-2 mb-4 flex-wrap">
      <span className="text-xs font-semibold px-3 py-1 rounded-full bg-teal-100 text-teal-700">MultiSellerPro</span>
      <span className="text-xs font-semibold px-3 py-1 rounded-full bg-blue-100 text-blue-700">MultiInfluencerPro</span>
    </div>
  );

  const BundleButton = ({ tier, style }) => (
    <button
      onClick={() => onUpgrade(tier, 'bundle')}
      className={`w-full py-4 rounded-xl text-lg font-semibold transition shadow-lg mb-8 text-white
        ${isCurrentPlan(tier) ? '!bg-gray-300 !text-gray-600' : 'hover:opacity-90'}`}
      style={!isCurrentPlan(tier) ? style : {}}
    >
      {ctaLabel(tier)}
    </button>
  );

  return (
    <>
      {/* Beginner Bundle */}
      <div className="bg-white rounded-3xl shadow-lg p-8 border border-gray-200 hover:shadow-xl transition-shadow relative">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Bundle Beginner</h2>
        <ProductChips />
        <PriceBlock tier="Beginner" billingPeriod={billingPeriod} priceClass={t.priceClass} isBundle />
        <BundleButton tier="Beginner" style={t.ctaFillStyle} />
        <div className="border-t border-gray-200 pt-6">
          <h3 className="text-sm font-semibold text-gray-500 mb-4">All features included:</h3>
          <FeatureList features={[...MSP_BEGINNER_FEATURES, ...MIP_BEGINNER_FEATURES]} checkColor={t.checkColor} />
        </div>
      </div>

      {/* Pro Bundle */}
      <div className={`bg-white rounded-3xl shadow-xl p-8 border-2 ${t.featuredBorder} relative hover:shadow-2xl transition-shadow`}>
        <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-gradient-to-r from-amber-500 to-orange-600 text-white text-sm font-bold px-6 py-2 rounded-full shadow-lg whitespace-nowrap">
          Best Value
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2 mt-2">Bundle Pro</h2>
        <ProductChips />
        <PriceBlock tier="Pro" billingPeriod={billingPeriod} priceClass={t.priceClass} isBundle />
        <BundleButton tier="Pro" style={t.ctaFillStyle} />
        <div className="border-t border-gray-200 pt-6">
          <h3 className="text-sm font-semibold text-gray-500 mb-4">Everything in Beginner, plus:</h3>
          <FeatureList features={[...MSP_PRO_FEATURES, ...MIP_PRO_FEATURES]} checkColor={t.checkColor} />
        </div>
      </div>

      {/* Unlimited Bundle */}
      <div className="bg-white rounded-3xl shadow-2xl p-8 border-2 border-amber-400 relative hover:shadow-3xl transition-shadow">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Bundle Unlimited</h2>
        <ProductChips />
        <PriceBlock tier="Unlimited" billingPeriod={billingPeriod} priceClass={t.priceClass} isBundle />
        <BundleButton tier="Unlimited" style={{ background: 'linear-gradient(90deg, #92400e, #b45309)' }} />
        <div className="border-t border-gray-200 pt-6">
          <h3 className="text-sm font-semibold text-gray-500 mb-4">Everything in Pro, plus:</h3>
          <FeatureList features={[...MSP_UNLIMITED_FEATURES.filter(f => !MIP_UNLIMITED_FEATURES.some(m => m.text === f.text)), ...MIP_UNLIMITED_FEATURES]} checkColor={t.checkColor} />
        </div>
      </div>
    </>
  );
};

/* ─────────────────────────────────────────────
   Product Selector
───────────────────────────────────────────── */
const ProductSelector = ({ active, onChange }) => {
  const options = [
    { key: 'msp', icon: '🛒', label: 'Seller Pro', sub: 'For sellers', activeClass: 'bg-gradient-to-br from-teal-50 to-blue-50 border-teal-300' },
    { key: 'bundle', icon: '🔥', label: 'Bundle', sub: 'Both products', activeClass: 'bg-gradient-to-br from-amber-50 to-orange-50 border-amber-300', badge: 'SAVE 20%' },
    { key: 'mip', icon: '🎬', label: 'Influencer Pro', sub: 'For influencers', activeClass: 'bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-400' },
  ];

  return (
    <div className="flex justify-center mb-10">
      <div className="bg-white rounded-3xl shadow-lg border border-gray-200 flex overflow-hidden max-w-2xl w-full">
        {options.map((opt, i) => (
          <button
            key={opt.key}
            onClick={() => onChange(opt.key)}
            className={`flex-1 py-5 px-4 flex flex-col items-center gap-1 transition-all relative
              ${i < options.length - 1 ? 'border-r border-gray-100' : ''}
              ${active === opt.key ? opt.activeClass + ' border' : 'hover:bg-gray-50'}`}
          >
            {opt.badge && (
              <span className="absolute -top-px left-1/2 -translate-x-1/2 bg-green-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-b-lg">
                {opt.badge}
              </span>
            )}
            <span className="text-2xl mt-1">{opt.icon}</span>
            <span className="font-bold text-gray-900 text-sm text-center leading-tight">{opt.label}</span>
            <span className="text-xs text-gray-500">{opt.sub}</span>
            <span className={`w-2 h-2 rounded-full mt-1 ${active === opt.key
              ? opt.key === 'msp' ? 'bg-teal-500' : opt.key === 'mip' ? 'bg-blue-600' : 'bg-amber-500'
              : 'bg-gray-200'
              }`} />
          </button>
        ))}
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────
   Main PricingPage
───────────────────────────────────────────── */
const preferredToolToProduct = (pref) => {
  if (pref === 'seller') return 'msp';
  if (pref === 'influencer') return 'mip';
  if (pref === 'bundle') return 'bundle';
  return null;
};

const productToPreferredTool = (product) => {
  if (product === 'msp') return 'seller';
  if (product === 'mip') return 'influencer';
  return 'bundle';
};

const ProductChoiceModal = ({ onChoose, onClose }) => {
  const options = [
    { key: 'msp', icon: '🛒', title: 'Seller Pro', desc: 'For selling - source, scan, and manage inventory across Amazon, Walmart, and eBay.', gradient: 'linear-gradient(90deg, rgb(20,184,166) 0%, rgb(37,99,235) 100%)' },
    { key: 'bundle', icon: '🔥', title: 'Bundle', desc: 'Get both Seller Pro and Influencer Pro together, at 20% off.', gradient: 'linear-gradient(90deg, #b45309, #c2410c)' },
    { key: 'mip', icon: '🎬', title: 'Influencer Pro', desc: 'For Amazon Influencers (AIP) - video tools, mobile app, and creator connections.', gradient: 'linear-gradient(135deg, #2563eb, #7c3aed)' },
  ];
  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-60 z-50 animate-fadeIn" onClick={onClose} />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full p-8 relative animate-fadeIn">
          <button onClick={onClose} className="absolute top-4 right-4 w-9 h-9 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-full flex items-center justify-center transition">✕</button>
          <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">Which one is for you?</h2>
          <div className="grid sm:grid-cols-3 gap-4">
            {options.map((opt) => (
              <button key={opt.key} onClick={() => onChoose(opt.key)} className="text-left rounded-xl border-2 border-gray-200 hover:border-gray-300 p-5 transition hover:shadow-lg">
                <span className="text-3xl">{opt.icon}</span>
                <h3 className="text-lg font-bold text-gray-900 mt-3 mb-1">{opt.title}</h3>
                <p className="text-gray-500 text-sm mb-4">{opt.desc}</p>
                <span className="inline-block text-white text-xs font-semibold px-3 py-1.5 rounded-full" style={{ background: opt.gradient }}>Choose {opt.title}</span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </>
  );
};

const PricingPage = ({ isAuthenticated, user }) => {
  const navigate = useNavigate();
  const [billingPeriod, setBillingPeriod] = useState('annual');
  const [freeTrialDays, setFreeTrialDays] = useState(7);
  const isNonUS = user?.amazon_marketplace && user.amazon_marketplace !== 'US';
  const [activeProduct, setActiveProduct] = useState(() => {
    if (isNonUS) return 'msp';
    return preferredToolToProduct(localStorage.getItem('preferredTool')) || 'bundle';
  });
  const [showProductChoiceModal, setShowProductChoiceModal] = useState(
    !isNonUS && !localStorage.getItem('preferredTool')
  );
  const [showPlaylist, setShowPlaylist] = useState(false);
  const [playlistPlan, setPlaylistPlan] = useState('Beginner');
  const [playlistProduct, setPlaylistProduct] = useState('msp');

  const handleChooseProduct = (product) => {
    localStorage.setItem('preferredTool', productToPreferredTool(product));
    setActiveProduct(product);
    setShowProductChoiceModal(false);
  };

  const handleCloseProductChoiceModal = () => {
    localStorage.setItem('preferredTool', 'bundle');
    setActiveProduct('bundle');
    setShowProductChoiceModal(false);
  };

  useEffect(() => {
    if (isNonUS || (isAuthenticated && user?.subscription_id && user.subscription_id !== 'none')) {
      setFreeTrialDays(0);
    }
  }, [isAuthenticated, user, isNonUS]);

  const handleUpgrade = (tier, product) => {
    if (!isAuthenticated || !user) {
      navigate('/login');
      return;
    }
    const hasHadTrial = isNonUS || (user.subscription_id && user.subscription_id !== 'none');
    const idMap = product === 'msp' ? MSP_PRODUCT_IDS : product === 'mip' ? MIP_PRODUCT_IDS : BUNDLE_PRODUCT_IDS;
    const url = buildCheckoutUrl(idMap, tier, billingPeriod, hasHadTrial, user.email);
    window.location.href = url;
  };

  const handleDemo = (plan, product) => {
    setPlaylistPlan(plan);
    setPlaylistProduct(product);
    setShowPlaylist(true);
  };

  // Billing toggle accent color
  const billingActiveStyle = {
    msp: { background: 'linear-gradient(90deg, rgb(20,184,166) 0%, rgb(37,99,235) 100%)' },
    mip: { background: 'linear-gradient(135deg, #2563eb, #7c3aed)' },
    bundle: { background: 'linear-gradient(90deg, #b45309, #c2410c)' },
  }[activeProduct];

  // Page title
  const titles = {
    msp: { main: 'Get Seller Pro Today!', sub: 'The ultimate toolkit for multi-platform sellers.' },
    mip: { main: 'Get Influencer Pro Today!', sub: 'The complete platform for Amazon Influencers.' },
    bundle: { main: 'Get the Bundle Today!', sub: 'Both products. One price. 20% off.' },
  };
  const { main, sub } = titles[activeProduct];

  // Title gradient
  const titleGradient = {
    msp: 'from-teal-500 via-teal-600 to-blue-600',
    mip: 'from-blue-600 via-indigo-600 to-purple-600',
    bundle: 'from-amber-600 via-orange-600 to-red-600',
  }[activeProduct];

  const modalTutorials = playlistProduct === 'msp' ? MSP_TUTORIALS : MIP_TUTORIALS;
  const modalGradient = playlistProduct === 'msp' ? 'from-red-600 to-pink-600' : 'from-blue-700 to-purple-700';
  const modalTitle = playlistProduct === 'msp' ? 'MultiSellerPro' : 'MultiInfluencerPro';

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 py-16 px-4">
      {showProductChoiceModal && (
        <ProductChoiceModal onChoose={handleChooseProduct} onClose={handleCloseProductChoiceModal} />
      )}
      <style>{`
        @keyframes fadeIn { from { opacity: 0; transform: translateY(5px); } to { opacity: 1; transform: translateY(0); } }
        .animate-fadeIn { animation: fadeIn 0.2s ease-out; }
        .section-fade { animation: fadeIn 0.25s ease-out; }
        .blur-plan { filter: blur(4px); pointer-events: none; user-select: none; }
      `}</style>

      {/* Background blobs */}
      <div className="fixed inset-0 opacity-10 pointer-events-none">
        <div className="absolute top-0 -left-4 w-96 h-96 rounded-full filter blur-3xl"
          style={{ background: 'linear-gradient(90deg, rgb(20,184,166) 0%, rgb(37,99,235) 100%)' }} />
        <div className="absolute top-0 -right-4 w-96 h-96 rounded-full filter blur-3xl"
          style={{ background: 'linear-gradient(90deg, rgb(20,184,166) 0%, rgb(37,99,235) 100%)' }} />
        <div className="absolute -bottom-8 left-20 w-96 h-96 rounded-full filter blur-3xl"
          style={{ background: 'linear-gradient(90deg, rgb(20,184,166) 0%, rgb(37,99,235) 100%)' }} />
      </div>

      <div className="max-w-7xl mx-auto relative z-10">

        {/* Title */}
        <div className="text-center mb-10">
          <h1 className={`text-5xl font-bold mb-2 bg-gradient-to-r ${titleGradient} bg-clip-text text-transparent transition-all`}>
            {main}
          </h1>
          <p className="text-gray-600 text-lg">{sub}</p>
        </div>

        {/* Product selector */}
        {!isNonUS && <ProductSelector active={activeProduct} onChange={handleChooseProduct} />}

        {/* Billing toggle */}
        <div className="flex justify-center mb-12">
          <div className="bg-white rounded-full p-1 flex gap-1 shadow-lg border border-gray-200">
            <button
              onClick={() => setBillingPeriod('monthly')}
              className={`px-8 py-3 rounded-full font-semibold transition ${billingPeriod === 'monthly' ? 'text-white shadow-md' : 'text-gray-700 hover:bg-gray-50'}`}
              style={billingPeriod === 'monthly' ? billingActiveStyle : {}}
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingPeriod('annual')}
              className={`px-8 py-3 rounded-full font-semibold transition relative ${billingPeriod === 'annual' ? 'text-white shadow-md' : 'text-gray-700 hover:bg-gray-50'}`}
              style={billingPeriod === 'annual' ? billingActiveStyle : {}}
            >
              Annual
              <span className="absolute -top-2 -right-2 bg-green-500 text-white text-xs font-bold px-2 py-1 rounded-full">
                2 MONTHS FREE
              </span>
            </button>
          </div>
        </div>

        {/* Cards */}
        <div className="grid md:grid-cols-3 gap-6 max-w-7xl mx-auto section-fade" key={activeProduct}>
          {activeProduct === 'msp' && user?.amazon_marketplace && user.amazon_marketplace !== 'US' ? (
            <NonUSCard
              billingPeriod={billingPeriod}
              user={user}
              onUpgrade={handleUpgrade}
              marketplaceCode={user.amazon_marketplace}
            />
          ) : activeProduct === 'msp' && (
            <MSPCards
              billingPeriod={billingPeriod}
              freeTrialDays={freeTrialDays}
              user={user}
              onUpgrade={handleUpgrade}
              onDemo={handleDemo}
            />
          )}
          {activeProduct === 'mip' && (
            <MIPCards
              billingPeriod={billingPeriod}
              freeTrialDays={freeTrialDays}
              user={user}
              onUpgrade={handleUpgrade}
              onDemo={handleDemo}
            />
          )}
          {activeProduct === 'bundle' && (
            <BundleCards
              billingPeriod={billingPeriod}
              freeTrialDays={freeTrialDays}
              user={user}
              onUpgrade={handleUpgrade}
            />
          )}
        </div>

        <br /><br /><br />
        <Quotes />

        <div className="mt-12 text-center">
          <p className="text-black text-lg">
            You know how they say 'a picture is worth a thousand words'? In this case, it's a video:
          </p>
          <a href="https://www.youtube.com/watch?v=0d-eZEVcKRo" target="_blank"
            rel="noopener noreferrer" className="text-blue-600 text-lg underline">MultiSellerPro - The Fat Stanley Song (Official Video)</a>
        </div>

        {/* Footer info */}
        <div className="mt-14 text-center">
          <div className="bg-teal-50 border border-teal-200 rounded-lg p-4 max-w-2xl mx-auto mb-4">
            <p className="text-teal-900 font-medium mb-1">Earn 30% Commission</p>
            <p className="text-teal-700 text-sm">
              Sign up, go to Settings, grab your affiliate link, and start earning!
            </p>
          </div>
          <p className="text-gray-600 text-lg mb-4">
            Need any help? Reach us anytime at <strong>contact@multisellerpro.com</strong>
          </p>
          {freeTrialDays > 0 && (
            <p className="text-gray-600 text-sm mb-3.5">
              All plans include a {freeTrialDays}-day free trial. Cancel anytime, no questions asked.
            </p>
          )}
          <p className="text-gray-500 text-xs">
            Prices shown in USD. Additional taxes may apply based on your location.
          </p>
          <p className="text-gray-500 text-xs">
            14-day money-back guarantee on all paid plans.
          </p>
        </div>
      </div>

      {/* Video Modal */}
      <VideoModal
        show={showPlaylist}
        onClose={() => setShowPlaylist(false)}
        tutorials={modalTutorials}
        plan={playlistPlan}
        title={modalTitle}
        gradient={modalGradient}
      />
    </div>
  );
};

export default PricingPage;